import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Form, Input } from 'antd'
import { OTPRef } from 'antd/es/input/OTP'

import IntlMessages from '../../../../util/IntlMessages'

export const CODE_LENGTH = 6

export interface ICodeFieldHandle {
  focus: () => void
}

export interface ICodeFieldProps {
  disabled: boolean
  /** Named by the step: an authenticator code and an emailed one are not the same thing. */
  requiredMessageId: string
  dataQa: string
  /** Any keystroke, so the step can drop what it said about the previous code. */
  onChanged: () => void
  /** All six boxes filled. */
  onComplete?: () => void
  className?: string
}

/**
 * The six-box code entry, shared by the sign-in challenge and the password reset. Reads the
 * form from context rather than taking it as a prop, so a step only has to place it.
 */
const CodeField = forwardRef<ICodeFieldHandle, ICodeFieldProps>(({ disabled, requiredMessageId, dataQa, onChanged, onComplete, className }, ref) => {
  const form = Form.useFormInstance()
  const inputRef = useRef<OTPRef>(null)

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }))

  return (
    <Form.Item
      name="code"
      className={className ? `code-field ${className}` : 'code-field'}
      rules={[
        { required: true, message: <IntlMessages id={requiredMessageId} /> },
        { pattern: /^[0-9]{6}$/, message: <IntlMessages id="form.inputSize" values={{ len: CODE_LENGTH }} /> },
      ]}
    >
      <Input.OTP
        length={CODE_LENGTH}
        inputMode="numeric"
        ref={inputRef}
        // Session recordings mask password fields only, and these boxes are plain text.
        className="ph-no-capture"
        disabled={disabled}
        // The OTP only reports a complete value through the form, so anything that should
        // happen per keystroke has to happen here.
        onInput={() => {
          form.setFields([{ name: 'code', errors: [] }])
          onChanged()
        }}
        // Ant fires this only once all six are filled, and Form.Item runs its own handler
        // first, so the value is in the store by the time a caller reads it.
        onChange={() => onComplete?.()}
        data-qa={dataQa}
      />
    </Form.Item>
  )
})

CodeField.displayName = 'CodeField'

export default CodeField
